/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Quick Schedule Date Field
 *
 * @file QuickScheduleDateField.jsx
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import DateTextField from '../common/DateTextField';
import Icon from '../icons/Icon';
import { formatDateToStorage, parseDate } from '../../common/dateTimeUtils';
import { QUICK_SCHEDULE_FORM_DATE_FIELD_CLASS } from './quickScheduleFormConstants';
import { parseDateForPicker } from './quickScheduleFormUtils';

const toDisplayDateValue = (isoValue) => {
  if (!isoValue) {
    return '';
  }

  const pickerDate = parseDateForPicker(isoValue);

  return pickerDate ? formatDateToStorage(pickerDate) : '';
};

const QuickScheduleDateField = ({
  name,
  control,
  label,
  rules,
  minDate,
  maxDate,
  placeholderText,
  dateFormat,
  parentApp,
  toIso,
  onDateChange
}) => (
  <Controller
    name={name}
    control={control}
    rules={rules}
    render={({ field }) => {
      const handleChange = (event) => {
        const dateStr = event.target.value;

        if (!dateStr) {
          field.onChange('');
          onDateChange?.(dateStr, null, '');

          return;
        }

        const pickedDate = parseDate(dateStr);
        const iso = toIso(pickedDate, field.value);

        field.onChange(iso);
        onDateChange?.(dateStr, pickedDate, iso);
      };

      return (
        <DateTextField
          className={QUICK_SCHEDULE_FORM_DATE_FIELD_CLASS}
          label={label}
          value={toDisplayDateValue(field.value)}
          onChange={handleChange}
          parentApp={parentApp}
          minDate={minDate}
          maxDate={maxDate}
          placeholderText={placeholderText}
          dateFormat={dateFormat || 'MM/dd/yyyy'}
          icons={{ calendar: <Icon name="CalendarIcon" /> }}
        />
      );
    }}
  />
);

QuickScheduleDateField.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  rules: PropTypes.object,
  minDate: PropTypes.instanceOf(Date),
  /** Latest selectable calendar day; forwarded to DateTextField. */
  maxDate: PropTypes.instanceOf(Date),
  placeholderText: PropTypes.string,
  dateFormat: PropTypes.string,
  parentApp: PropTypes.string.isRequired,
  toIso: PropTypes.func.isRequired,
  onDateChange: PropTypes.func
};

QuickScheduleDateField.defaultProps = {
  rules: undefined,
  minDate: null,
  maxDate: null,
  placeholderText: '',
  dateFormat: '',
  onDateChange: null
};

export default QuickScheduleDateField;
